import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api, refreshAccessToken, clearSession } from '../services/apiClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [token, setToken] = useState(localStorage.getItem('token') || localStorage.getItem('test_token'));
  const [hasFetchedProfile, setHasFetchedProfile] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const fetchProfileRef = useRef(false); // Use ref to prevent duplicate calls in Strict Mode
  const hasInitializedRef = useRef(false); // Prevent double initialization in Strict Mode
  const refreshTokenRef = useRef(false); // Prevent concurrent refresh token calls
  const refreshTimerRef = useRef(null); // Timer for automatic token refresh

  // Sync token from localStorage on mount (prevent double execution in Strict Mode)
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    
    hasInitializedRef.current = true;
    const storedToken = localStorage.getItem('token') || localStorage.getItem('test_token');
    
    if (storedToken && storedToken !== token) {
      setToken(storedToken);
    }
  }, [token]); // Include token dependency

  // Hàm refresh token - gia hạn token mà không cần đăng nhập lại
  const refreshToken = useCallback(async (oldToken) => {
    if (refreshTokenRef.current) {
      return null; // Đang refresh, không gọi lại
    }

    if (!oldToken || oldToken.startsWith('test_token_')) {
      return null; // Không thể refresh test token
    }

    refreshTokenRef.current = true;

    try {
      // apiClient lưu token mới vào localStorage và phát sự kiện 'tokenRefreshed' (lắng nghe bên dưới)
      return await refreshAccessToken(oldToken);
    } finally {
      refreshTokenRef.current = false;
    }
  }, []);

  // Hàm decode JWT để lấy thông tin hết hạn
  const getTokenExpiration = useCallback((token) => {
    if (!token || token.startsWith('test_token_')) {
      return null;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
      return null;
    }
  }, []);

  // Hàm tự động refresh token trước khi hết hạn
  const scheduleTokenRefresh = useCallback((currentToken) => {
    // Xóa timer cũ nếu có
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (!currentToken || currentToken.startsWith('test_token_')) {
      return;
    }

    const expirationTime = getTokenExpiration(currentToken);
    if (!expirationTime) {
      return;
    }

    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;

    // Refresh token trước khi hết hạn 1 ngày (24 giờ)
    // Hoặc nếu còn ít hơn 1 ngày, refresh sau 80% thời gian còn lại
    const refreshTime = Math.max(
      timeUntilExpiration - (24 * 60 * 60 * 1000), // 1 ngày trước khi hết hạn
      timeUntilExpiration * 0.8 // Hoặc 80% thời gian còn lại (nếu ít hơn 1 ngày)
    );

    if (refreshTime > 0) {
      refreshTimerRef.current = setTimeout(() => {
        refreshToken(currentToken).then((newToken) => {
          if (newToken) {
            // Lên lịch refresh tiếp cho token mới
            scheduleTokenRefresh(newToken);
          }
        });
      }, refreshTime);
    } else {
      // Token sắp hết hạn, refresh ngay
      refreshToken(currentToken).then((newToken) => {
        if (newToken) {
          scheduleTokenRefresh(newToken);
        }
      });
    }
  }, [refreshToken, getTokenExpiration]);

  const fetchUserProfile = useCallback(async () => {
    if (isFetching || fetchProfileRef.current) {
      return; // Prevent duplicate calls
    }
    
    fetchProfileRef.current = true; // Set ref immediately
    setIsFetching(true);
    
    try {
      // Kiểm tra nếu là test token
      if (token && token.startsWith('test_token_')) {
        const testUser = {
          id: 'test_user_id',
          email: 'test@example.com',
          name: 'User Test User',
          avatar_url: 'https://via.placeholder.com/150',
          role: 'USER',
          is_active: true,
          last_login_at: new Date().toISOString()
        };
        setUser(testUser);
        setLoading(false);
        return;
      }

      // Nếu là token thật, gọi API. apiClient tự refresh token khi gặp 401;
      // vẫn 401 thì đăng xuất và về /login.
      const { data: me } = await api.get('/me', { onUnauthorized: 'throw' });

      if (me && me.id) {
        setUser(me);
        try { localStorage.setItem('user', JSON.stringify(me)); } catch (_) {}
      } else {
        throw new Error('Invalid user response');
      }
    } catch (error) {
      if (error.status === 401 && token && !token.startsWith('test_token_')) {
        clearSession();
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
      setIsFetching(false);
      fetchProfileRef.current = false; // Reset ref
    }
  }, [token, isFetching]);

  // Lắng nghe sự kiện login từ GoogleCallback
  useEffect(() => {
    const handleUserLogin = (event) => {
      const { token: newToken, userData } = event.detail;
      setToken(newToken);
      if (userData) {
        setUser(userData);
      }
    };
    // apiClient gia hạn token (tự động khi gặp 401 hoặc theo lịch) -> đồng bộ state
    const handleTokenRefreshed = (event) => {
      const { token: newToken, user: refreshedUser } = event.detail || {};
      if (newToken) setToken(newToken);
      if (refreshedUser) setUser((prev) => ({ ...(prev || {}), ...refreshedUser }));
    };

    window.addEventListener('userLogin', handleUserLogin);
    window.addEventListener('tokenRefreshed', handleTokenRefreshed);
    return () => {
      window.removeEventListener('userLogin', handleUserLogin);
      window.removeEventListener('tokenRefreshed', handleTokenRefreshed);
    };
  }, []);

  // Fetch user profile + lên lịch refresh token
  useEffect(() => {
    if (token && !hasFetchedProfile && !isFetching) {
      if (!token.startsWith('test_token_')) {
        scheduleTokenRefresh(token);
      }
      fetchUserProfile();
      setHasFetchedProfile(true);
    } else if (!token) {
      setLoading(false);
      // Xóa timer nếu không có token
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }
  }, [token, fetchUserProfile, hasFetchedProfile, isFetching, scheduleTokenRefresh]); // Include all dependencies

  // Cleanup timer khi component unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const login = (newToken) => {
    setToken(newToken);
    setHasFetchedProfile(false); // Reset fetch profile flag
    setIsFetching(false); // Reset fetching flag
    setLoading(true); // Set loading to true initially
    fetchProfileRef.current = false; // Reset ref
    
    if (newToken.startsWith('test_token_')) {
      localStorage.setItem('test_token', newToken);
    } else {
      localStorage.setItem('token', newToken);
      // Lên lịch tự động refresh token khi đăng nhập
      scheduleTokenRefresh(newToken);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setHasFetchedProfile(false);
    setIsFetching(false);
    setLoading(false); // ensure UI doesn't get stuck on loading
    fetchProfileRef.current = false; // Reset ref
    
    // Xóa timer refresh token
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('test_token');
    localStorage.removeItem('test_user_role');
    localStorage.removeItem('user'); // Also remove user data
  };

  // Cập nhật vài trường của user sau khi sửa hồ sơ (vd ảnh đại diện) để TopBar đổi theo, không cần gọi lại /me
  const updateUser = (fields) => {
    setUser((prev) => (prev ? { ...prev, ...fields } : prev));
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
